import React, { useMemo, useState } from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import DrugAdminPopup from './DrugAdminPopup'

import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import InputAdornment from '@mui/material/InputAdornment'
import Modal from '@mui/material/Modal'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import SearchIcon from '@mui/icons-material/Search'
import { Chip, Grid } from '@mui/material'

// Medicine expiry is month-based (see ExpiryDatePicker), so show MM/YYYY.
const BatchChip = ({ qty, title, batchNumber, expiry }) => {
  const expiryLabel =
    expiry && moment(expiry).isValid() ? moment(expiry).format('MM/YYYY') : null
  return (
    <Chip
      size="small"
      variant="outlined"
      sx={{ height: 'auto', py: 0.25, '& .MuiChip-label': { whiteSpace: 'normal' } }}
      label={
        <span>
          <strong>{qty} ×</strong>
          {title ? ` ${title}` : ''}
          {batchNumber ? ` · Batch ${batchNumber}` : ''}
          {expiryLabel ? ` · Exp ${expiryLabel}` : ''}
        </span>
      }
    />
  )
}

const Log = ({ logs = [], title }) => {
  const navigate = useNavigate()

  const sortedLogs = logs.sort((a, b) =>
    a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0,
  )
  const [viewLog, setViewLog] = useState(null)
  const [query, setQuery] = useState('')

  const filteredLogs = useMemo(() => {
    if (!query.trim()) return sortedLogs
    const q = query.toLowerCase()

    return sortedLogs.filter((log) => {
      const who = `${log.person?.firstName ?? ''} ${
        log.person?.lastName ?? ''
      }`.toLowerCase()
      const note = (log.note ?? '').toLowerCase()

      // 🔹 Audit discrepancies — titles + batches
      const auditText = (log.auditDiscrepancies ?? [])
        .map((x) => `${x.title ?? ''} ${x.batch ?? ''}`.toLowerCase())
        .join(' ')

      // 🔹 Affected meds — titles + batches
      // Newer logs key on batch+expiry and carry `batchNumber` on the
      // value; legacy logs are keyed on the bare batch number.
      const affectedText = Object.keys(log.affectedMeds ?? {})
        .map((batch) => {
          const med = log.affectedMeds[batch]
          return `${med.title ?? ''} ${med.batchNumber ?? batch ?? ''}`.toLowerCase()
        })
        .join(' ')

      // 🔹 Combine all searchable text
      return (
        who.includes(q) ||
        note.includes(q) ||
        auditText.includes(q) ||
        affectedText.includes(q)
      )
    })
  }, [logs, query])

  return (
    <Paper
      elevation={1}
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: '1px solid rgba(15,23,42,0.08)',
        backgroundColor: '#fff',
        '& > .MuiPaper-root': {
          boxShadow: 'none',
        },
      }}
    >
      <Box padding={1} display="flex" flexDirection="column" gap={1}>
        <Grid container spacing={1}>
          <Grid item xs={12} md={4} lg={6}>
            <Typography variant="title2">{title || 'Logs'}</Typography>
          </Grid>
          <Grid item xs={12} md={8} lg={6}>
            <TextField
              size="small"
              autoComplete="none"
              placeholder="Search logs…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              fullWidth
              sx={{ my: 1 }}
            />
          </Grid>
        </Grid>
        <Stack
          divider={<Divider />}
          direction="column"
          gap={1}
          maxHeight={200}
          overflow="auto"
        >
          {filteredLogs.map((log) => (
            <Box display="flex" gap={3} key={log._id}>
              <Box
                display="flex"
                flex={0.4}
                minWidth={200}
                justifyContent="flex-start"
                alignItems="flex-start"
              >
                <Stack direction="row" gap={1}>
                  <Avatar
                    sx={{ width: 32, height: 32, borderRadius: 1 }}
                    src={log.person?.photograph}
                  />
                  <Box
                    display="flex"
                    flexDirection={{ xs: 'column', lg: 'row' }}
                    alignItems={{ xs: 'flex-start', lg: 'center' }}
                    gap={{ xs: 0, lg: 4 }}
                  >
                    <Box
                      fontSize="0.8rem"
                      display="flex"
                      gap={{ xs: 1, lg: 0 }}
                      flexDirection={{ xs: 'row', lg: 'column' }}
                    >
                      <Box fontWeight="bold" fontStyle="italic">
                        {moment(log.createdAt).format('Do MMM YYYY HH:mm')}
                      </Box>
                      <Box>
                        {log.person?.firstName} {log.person?.lastName}
                      </Box>
                    </Box>
                  </Box>
                </Stack>
              </Box>
              <Box display="flex" flex={1}>
                <Stack>
                  <Box>{log.note}</Box>
                  <Stack direction="row" gap={0.75} flexWrap="wrap" mt={0.5}>
                    {(log?.auditDiscrepancies || [])?.map((x) => (
                      <BatchChip
                        key={x._id}
                        qty={x.quantity}
                        title={x.title}
                        batchNumber={x.batch}
                        expiry={x.expiry}
                      />
                    ))}
                    {Object.entries(log.affectedMeds ?? {}).map(
                      ([key, med]) => (
                        <BatchChip
                          key={key}
                          qty={med.qty}
                          title={med.title}
                          batchNumber={med.batchNumber ?? key}
                          expiry={med.expiry}
                        />
                      ),
                    )}
                  </Stack>
                </Stack>
              </Box>
              <Box
                flex={0.22}
                justifyContent="flex-end"
                alignItems="center"
                display="flex"
                minWidth={150}
                border="soid 1px red"
              >
                <Box alignItems="flex-end" display="flex" width="100%">
                  {log.audit && (
                    <Button
                      variant="outlined"
                      color="anPurple"
                      onClick={() =>
                        navigate(`/medicines-management/audits/${log.audit}`)
                      }
                      fullWidth
                    >
                      View Audit
                    </Button>
                  )}
                  {log.epcr ? (
                    <Button
                      variant="outlined"
                      color="anBlue"
                      onClick={() =>
                        window.open(`/patient-report-forms/${log.epcr}`)
                      }
                      fullWidth
                    >
                      View ePCR
                    </Button>
                  ) : log.administeredBy ? (
                    <Button
                      variant="outlined"
                      color="anBlue"
                      onClick={() => setViewLog(log)}
                      fullWidth
                    >
                      View Details
                    </Button>
                  ) : (
                    log.incident && (
                      <Button
                        variant="outlined"
                        color="anBlue"
                        onClick={() => navigate(`/incidents/${log.incident}`)}
                        fullWidth
                      >
                        View Incident
                      </Button>
                    )
                  )}
                </Box>
              </Box>
            </Box>
          ))}
        </Stack>
      </Box>
      <Modal open={viewLog} onClose={() => setViewLog(null)}>
        <DrugAdminPopup viewLog={viewLog} />
      </Modal>
    </Paper>
  )
}

export default Log
